// Sales Team performance, computed from CRM assignment (never the Sheet's
// Account Manager) joined to Sheet leads by the canonical lead key.

import { leadKeyFor } from '../../data/unifiedLeads'
import type { Lead } from '../../types/crm'
import type { LeadAssignment, StaffMember } from '../leads/leadCrmClient'

export type SalespersonMetrics = {
  staff: StaffMember
  /** Profiles whose assigned_staff_id is this person. */
  assigned: number
  /** Assigned and not Won/Lost; an unset status is still open work. */
  active: number
  won: number
  lost: number
  /** Sheet Opportunity Value summed over Won leads that have a real value. */
  closedValue: number
  /** Won leads counted in `won` but left out of `closedValue`. */
  wonWithoutValue: number
}

export type TeamMetrics = {
  rows: SalespersonMetrics[]
  totals: { members: number; assigned: number; won: number; closedValue: number; wonWithoutValue: number }
}

/**
 * The Sheet parser turns a blank or unparseable value into 0, so only a
 * positive finite number is treated as revenue.
 */
function closedValueOf(lead: Lead | undefined): number | null {
  const value = lead?.opportunity?.value
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

export function computeTeamMetrics(
  staff: StaffMember[],
  assignments: Record<string, LeadAssignment>,
  leads: Lead[],
): TeamMetrics {
  const leadByKey = new Map<string, Lead>()
  for (const lead of leads) {
    const key = leadKeyFor(lead)
    if (key && !leadByKey.has(key)) leadByKey.set(key, lead)
  }

  // Only active salespeople are listed, matching who can be assigned.
  const rows = staff
    .filter((member) => member.active)
    .map<SalespersonMetrics>((member) => ({ staff: member, assigned: 0, active: 0, won: 0, lost: 0, closedValue: 0, wonWithoutValue: 0 }))
  const byId = new Map(rows.map((row) => [row.staff.id, row]))

  for (const [leadKey, assignment] of Object.entries(assignments)) {
    const row = byId.get(assignment.assignedStaffId)
    if (!row) continue

    row.assigned += 1
    if (assignment.crmStatus === 'Won') {
      row.won += 1
      const value = closedValueOf(leadByKey.get(leadKey))
      if (value === null) row.wonWithoutValue += 1
      else row.closedValue += value
    } else if (assignment.crmStatus === 'Lost') {
      row.lost += 1
    } else {
      row.active += 1
    }
  }

  const sum = (pick: (row: SalespersonMetrics) => number) => rows.reduce((total, row) => total + pick(row), 0)

  return {
    rows,
    totals: {
      members: rows.length,
      assigned: sum((row) => row.assigned),
      won: sum((row) => row.won),
      closedValue: sum((row) => row.closedValue),
      wonWithoutValue: sum((row) => row.wonWithoutValue),
    },
  }
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  const letters = parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts[parts.length - 1][0]}`
  return letters.toUpperCase()
}
